import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import './tabs.styles.scss';
import { ITab } from 'models';
import { Button } from 'components/base';
import {
	isLoadingSelector,
	isOfficesAndLimitsDataLoadingSelector,
	isSurveysLoading,
} from 'modules/admin-panel/pages/templates/selectors';

interface TabsProps {
	activeTabId?: number | string;
	tabList: ITab[];
	onChangeTab: (tabId: number | string) => void;
	onClickCancel?: () => void;
	onFinish?: () => void;
	wrapperClass?: string;
	contentClass?: string;
	lastTabBtnText?: string;
	isLastValid?: boolean;
}

/**
 * Provide tabs' content as children with 'data-tab-id' attribute
 * ['data-tab-id']: number | string
 */
export const Tabs: React.FC<TabsProps> = ({
	children,
	tabList,
	activeTabId,
	onClickCancel = () => {},
	onFinish = () => {},
	wrapperClass = '',
	contentClass = '',
	onChangeTab,
	lastTabBtnText,
	isLastValid,
}) => {
	const { t } = useTranslation('common');
	const isOfficesAndLimitsDataLoading = useSelector(
		isOfficesAndLimitsDataLoadingSelector,
	);
	const isLoading = useSelector(isLoadingSelector);
	const isSurveyLoading = useSelector(isSurveysLoading);

	const getActiveTabIdx = useCallback(
		(id: string | number) => {
			return React.Children.toArray(children).findIndex((el) => {
				const tabId = (el as React.ReactElement).props['data-tab-id'];
				return tabId === id;
			});
		},
		[children],
	);

	const [activeIdx, setActiveIdx] = useState(getActiveTabIdx(tabList[0].tabId));
	const [isNextDisabled, setIsNextDisabled] = useState<boolean | undefined>(
		true,
	);
	const [isLastTab, setIsLastTab] = useState(false);

	const handleClickNext = () => {
		if (isLastTab && onFinish) {
			onFinish();
		} else {
			onChangeTab(tabList[activeIdx + 1].tabId);
		}
	};

	useEffect(() => {
		let newActiveTabIdx: number;
		if (activeTabId !== undefined) {
			newActiveTabIdx = getActiveTabIdx(activeTabId);
		} else {
			newActiveTabIdx = getActiveTabIdx(tabList[0].tabId);
		}

		setActiveIdx(newActiveTabIdx);
	}, [activeTabId, getActiveTabIdx, tabList]);

	useEffect(() => {
		setIsNextDisabled(tabList[activeIdx + 1]?.disabled);
	}, [tabList, activeIdx]);

	useEffect(() => {
		setIsLastTab(activeIdx === tabList.length - 1);
	}, [activeIdx, tabList]);

	return (
		<div className={`tabs ${wrapperClass}`}>
			<div className="tabs__header">
				{tabList.map((tab, idx) => (
					<Tab
						onClick={onChangeTab}
						tab={tab}
						key={tab.tabId}
						isPrevDisabled={isNextDisabled && idx > activeIdx}
						isActive={tab.tabId === tabList[activeIdx]?.tabId}
					/>
				))}
				<Button
					onClick={onClickCancel}
					buttonText={t('buttons.cancel')}
					buttonStyle="secondary"
					buttonClass="tabs__btn-control"
					toRight
				/>
				<Button
					onClick={handleClickNext}
					buttonText={
						isLastTab && lastTabBtnText ? lastTabBtnText : t('buttons.next')
					}
					buttonClass="tabs__btn-control"
					disabled={
						isNextDisabled ||
						(isLastTab && !isLastValid) ||
						isOfficesAndLimitsDataLoading ||
						isLoading ||
						isSurveyLoading
					}
				/>
			</div>
			<div className={classNames('tabs__content', contentClass)}>
				{React.Children.toArray(children)[activeIdx]}
			</div>
		</div>
	);
};

interface TabProps {
	tab: ITab;
	onClick: (tabId: number | string) => void;
	isPrevDisabled?: boolean;
	isActive: boolean;
}

function Tab({
	tab: { title, isVisited, tabId },
	onClick,
	isPrevDisabled,
	isActive,
}: TabProps) {
	function handleClick() {
		onClick(tabId);
	}

	return (
		<button
			type="button"
			onClick={handleClick}
			className={classNames('tabs__btn', {
				'tabs__btn--active': isActive,
				disabled: !isVisited || isPrevDisabled,
			})}
		>
			{title}
		</button>
	);
}
